import { useCallback, useEffect, useState } from "react";
import { detectBrandByDomain } from "../services/brandApi";

export interface DetectedBrandLegal {
  termsUrl: string | null;
  privacyUrl: string | null;
  termsVersion: string | null;
  privacyVersion: string | null;
}

export interface UseDetectedBrandLegalResult extends DetectedBrandLegal {
  /**
   * True until the FIRST `GET /brands/detect` call settles (success or
   * failure). Callers use this to render a calm loading placeholder instead
   * of "link niedostępny" while resolution is in flight (H8) — that message
   * is reserved for a genuine post-fetch failure. Does NOT flip back to
   * `true` on a later `refetch()` (e.g. after a `LEGAL_VERSION_MISMATCH`),
   * so an already-resolved link never disappears behind a loading state.
   */
  isLoading: boolean;
  /** Re-runs the detection — used to pick up the current version/URL after a 400 LEGAL_VERSION_MISMATCH. */
  refetch: () => Promise<void>;
}

const EMPTY: DetectedBrandLegal = {
  termsUrl: null,
  privacyUrl: null,
  termsVersion: null,
  privacyVersion: null,
};

/**
 * Fetches the current domain's brand-scoped legal URLs/versions from the
 * backend-resolved `GET /brands/detect` (ADR-088, spec
 * 2026-07-06-per-brand-legal-content AC B / H4) — the SINGLE source for
 * these, shared by `Register.tsx`, `Footer.tsx`, and `CookieBanner.tsx`.
 * Replaces the old `domainConfig.ts`-hardcoded `kiedynabory.pl` literals,
 * which could never be correct for a HotelB2B tenant on a custom domain.
 *
 * Returns `null` fields while loading and on failure — every render-site
 * treats a `null` URL as "unavailable" via `isSafeHttpUrl` (M6, fail-safe:
 * never show a broken or wrong-brand link instead of the real one), and a
 * caller that submits a version (Register) must guard against a `null`
 * version rather than send `undefined` to the backend. Use `isLoading` (H8)
 * to tell "still resolving" apart from "genuinely unavailable" — rendering
 * "link niedostępny" during the former is a flash bug, not a fail-safe.
 */
export function useDetectedBrandLegal(): UseDetectedBrandLegalResult {
  const [legal, setLegal] = useState<DetectedBrandLegal>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLegal = useCallback(async () => {
    try {
      const brand = await detectBrandByDomain(window.location.hostname);
      setLegal({
        termsUrl: brand.termsUrl ?? null,
        privacyUrl: brand.privacyUrl ?? null,
        termsVersion: brand.termsVersion ?? null,
        privacyVersion: brand.privacyVersion ?? null,
      });
    } catch {
      // Keep the previous (possibly still-empty) state — callers fall back
      // to "link niedostępny" via isSafeHttpUrl / block submit on a missing version.
    } finally {
      // H8: only the FIRST call needs to flip this — a later refetch() (e.g.
      // after LEGAL_VERSION_MISMATCH) must not re-trigger the loading state
      // for an already-displayed link.
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLegal();
  }, [fetchLegal]);

  return { ...legal, isLoading, refetch: fetchLegal };
}
